// src/workspace/RecheckPage.jsx
//
// Re-checking earlier wells on a new collect (build item 9, step 4; batch-lane
// design §5.1 and §10). Cells in a rail; a cell's items one at a time; each item
// opens on its OWN epoch, with the time strip to look at the others.
//
// At /workspace/recheck and /workspace/recheck/$cellX/$cellY. The landing's
// card (workspace/tools.js) is shown to a DreamHammer user holding
// wellfinder_reviewer or feature_reviewer, the callers the routes admit.
//
// The shape is FeatureCellPage's: a deep link to a cell selects it and takes
// its lock; a bare visit selects nothing. The lock is the server's to enforce
// here (a verdict without it is refused 409), so the page shows a cell held by
// someone else as read-only rather than offering buttons the server would
// refuse.
//
// One item at a time, and the cursor RESETS to the item's own epoch on every
// item: a reviewer who looked at 2025_12_13 on the last well must not judge the
// next one on it by accident. Moving the cursor is an explicit click. The
// verdict carries the epoch on screen (viewed_dataset_key) and the compare
// epoch, and the server refuses a verdict whose viewed epoch is not the item's.
//
// A CORRECTED POSITION (DB 2026-10-25, migration 142). m starts a draft at the
// item's point, where a verdict could be given (the lock held, the item's own
// collect on screen, not yet answered); the reviewer drags it on the map, and
// Confirm sends it as position, placed on the epoch on screen, up to 50 m off;
// past 10 m it is amber and the bar asks for a check (DB 2026-10-26, 144).
// Esc discards it.
// So does leaving the item's epoch, or the item, with a notice: a point placed
// on one collect is not carried to another.
//
// Shift+? opens the key map, WellFinder Review's overlay given this page's
// keys (recheckKeys.js, which the handler below also reads). Esc closes it
// first: with the map open, Esc closes it and leaves a draft alone.
//
// THE OPERATING AOI (DB 2026-10-25) is WellFinder Review's: its picker, its
// parameter, its stickiness. The selection is the URL's ?aoi=, which the cell
// list, the stats and the queue follow; a choice is remembered in a
// USER-scoped persisted store (useRecheckAoiPref) and restored on arrival by
// seeding the URL exactly as Review restores its own: the URL wins, the
// remembered id is checked against the loaded list and forgotten if it is gone,
// once per mount. Changing the AOI re-reads the open cell's items but keeps
// its lock.

import { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2, Lock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { recheckApi } from "../api/recheck";
import {
  TimeStrip, ReviewTimeStripMap, openingCursor, defaultCompare, verdictGate, formatEpoch, laneOf, normalizeLanes,
} from "../components/imagery/TimeStrip";
import RecheckVerdictBar from "../components/recheck/RecheckVerdictBar";
import { FOOTER_BUTTON } from "../components/recheck/footerButton";
import { RECHECK_KEY_ACTIONS, RECHECK_KEY_GROUPS, resolveRecheckKey } from "../components/recheck/recheckKeys";
import ShortcutHelp from "../components/wellfinder/ShortcutHelp";
import OperatingAoiPicker from "../components/OperatingAoiPicker";
import { useRecheckAoiPref } from "../stores/useRecheckAoiPref";
import { draftState, formatMeters, positionPayload, RECHECK_POSITION_LIMIT_M } from "../components/recheck/recheckPosition";

// Well inside the server's 30 minutes, as FeatureCellPage's.
const LOCK_REFRESH_MS = 8 * 60 * 1000;

const REFUSAL_TEXT = {
  item_closed: "This item was already answered, or its well is no longer approved.",
  lock_not_held: "You do not hold this cell's lock.",
  wrong_epoch: "The verdict must be given with the item's own collect on screen.",
  missing_reviewer_role: "Your account cannot give re-check verdicts.",
  note_required: "A reject needs a note.",
  wrong_epoch_for_position: "A corrected position is placed on the item's own collect.",
  position_unchanged: "The corrected position is the item's own point. Drag the marker, or press Esc.",
  recheck_position_not_installed: "Corrected positions are not installed on this environment yet (migration 142 is not applied).",
};

const DRAFT_DISCARDED = "The corrected position was discarded: it is placed on the item's own collect.";

function refusalText(r) {
  const b = r.body ?? {};
  if (b.code === "position_too_far") {
    return `The corrected position is ${b.distance_m} m from the item's point; it may be at most ${b.limit_m ?? RECHECK_POSITION_LIMIT_M} m.`;
  }
  return REFUSAL_TEXT[b.code] ?? b.detail ?? `The verdict was refused (${r.status}).`;
}

function pageStateFor(status, body) {
  if (status === 503 && body?.code === "recheck_not_installed") return "not_installed";
  if (status === 403) return "forbidden";
  return "error";
}

function epochLabel(lanes, key) {
  const l = normalizeLanes(lanes);
  const e = [...l.collects, ...l.reference].find((x) => x.dataset_key === key);
  return e ? formatEpoch(e.acquisition_date) : "";
}

export default function RecheckPage() {
  const { cellX, cellY } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const aoiId = search?.aoi ?? null;
  const navigate = useNavigate();
  const x = Number(cellX), y = Number(cellY);
  const hasCell = Number.isInteger(x) && Number.isInteger(y);

  const [page, setPage] = useState("loading");      // loading | ready | not_installed | forbidden | error
  const [cells, setCells] = useState([]);
  const [lock, setLock] = useState({ state: "none" }); // none | taking | held | held_by_other | error
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState(null);
  const [cursorKey, setCursorKey] = useState(null);
  const [compareKey, setCompareKey] = useState(null);
  const [decided, setDecided] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);         // [lon, lat] while a corrected position is placed
  const [aois, setAois] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // ── the operating AOI ─────────────────────────────────────────────────────
  //
  // The list, once per mount, as Review loads its own. A failure leaves the
  // picker with "All areas" only: the unfiltered queue.
  useEffect(() => {
    let live = true;
    recheckApi.operatingAois().then((r) => {
      if (!live || !r.ok) return;
      setAois(Array.isArray(r.body?.operating_aois) ? r.body.operating_aois : []);
      setTotalItems(r.body?.total_items ?? 0);
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  // Only the URL is written; the effects below follow it. undefined, not null,
  // so clearing the AOI removes the param. Remembered on every change, clearing
  // included, as Review remembers its own.
  const onAoiChange = useCallback((id) => {
    const aoi = id ?? undefined;
    navigate(hasCell
      ? { to: "/workspace/recheck/$cellX/$cellY", params: { cellX, cellY }, search: { aoi }, replace: true }
      : { to: "/workspace/recheck", search: { aoi }, replace: true });
    useRecheckAoiPref.getState().rememberAoi(id ?? null);
  }, [navigate, hasCell, cellX, cellY]);

  // Restore the last AOI by seeding the URL, as WellFinderReviewPage does and
  // for its reasons: the URL wins; the remembered id is checked against the
  // loaded list and forgotten if it is gone; once per mount, the guard set
  // before navigating.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (!aois.length) return;
    if (search?.aoi) { restoredRef.current = true; return; }
    const remembered = useRecheckAoiPref.getState().lastAoiId;
    if (!remembered) { restoredRef.current = true; return; }
    restoredRef.current = true;
    if (!aois.some((a) => a.id === remembered)) {
      useRecheckAoiPref.getState().rememberAoi(null);
      return;
    }
    onAoiChange(remembered);
  }, [aois, search?.aoi, onAoiChange]);

  // ── the rail ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    recheckApi.cells(aoiId).then((r) => {
      if (!live) return;
      if (r.ok) { setCells(r.body?.cells ?? []); setPage("ready"); } else setPage(pageStateFor(r.status, r.body));
    }).catch(() => live && setPage("error"));
    return () => { live = false; };
  }, [aoiId]);

  useEffect(() => {
    let live = true;
    setStats(null);
    recheckApi.stats(aoiId).then((r) => {
      if (live && r.ok) setStats(r.body);
    }).catch(() => {});
    return () => { live = false; };
  }, [aoiId]);

  // ── a cell: its lock ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasCell || page !== "ready") return undefined;
    let live = true;
    setNotice(null); setLock({ state: "taking" });
    recheckApi.lock(x, y).then((l) => {
      if (!live) return;
      if (l.ok) setLock({ state: "held" });
      else if (l.status === 409) setLock({ state: "held_by_other", by: l.body?.locked_by ?? null });
      else setLock({ state: "error", status: l.status });
    }).catch(() => live && setNotice("The cell could not be opened."));
    return () => {
      live = false;
      recheckApi.release(x, y).catch(() => {});
    };
  }, [x, y, hasCell, page]);

  // ── …and its items, in the AOI ────────────────────────────────────────────
  useEffect(() => {
    if (!hasCell || page !== "ready") return undefined;
    let live = true;
    setItems([]); setIndex(0); setDetail(null);
    recheckApi.queue(x, y, aoiId).then((q) => {
      if (!live) return;
      if (q.ok) setItems(q.body?.rows ?? []);
      else setNotice(`The cell's items could not be read (${q.status}).`);
    }).catch(() => live && setNotice("The cell could not be opened."));
    return () => { live = false; };
  }, [x, y, hasCell, page, aoiId]);

  useEffect(() => {
    if (lock.state !== "held") return undefined;
    const t = setInterval(() => { recheckApi.lock(x, y).catch(() => {}); }, LOCK_REFRESH_MS);
    return () => clearInterval(t);
  }, [lock.state, x, y]);

  // ── an item: opens on its own epoch ───────────────────────────────────────
  const current = items[index] ?? null;
  useEffect(() => {
    // A draft belongs to the item it was placed for.
    if (draft) { setDraft(null); setNotice(DRAFT_DISCARDED); }
    if (!current) { setDetail(null); setCursorKey(null); setCompareKey(null); return undefined; }
    let live = true;
    setDetail(null); setCursorKey(null); setCompareKey(null);
    recheckApi.item(current.item_id).then((r) => {
      if (!live) return;
      if (!r.ok) { setNotice(`The item could not be read (${r.status}).`); return; }
      const d = r.body;
      setDetail(d);
      setCursorKey(openingCursor(d.lanes, d.item.dataset_key));
      setCompareKey(defaultCompare(d.lanes, d.item.dataset_key, d.compare?.dataset_key ?? null));
    }).catch(() => live && setNotice("The item could not be read."));
    return () => { live = false; };
  }, [current?.item_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lanes = detail?.lanes ?? null;
  const itemKey = detail?.item?.dataset_key ?? null;
  const gate = verdictGate({ lanes, cursorKey, itemDatasetKey: itemKey });
  const origin = detail?.item?.geom?.coordinates ?? null;
  const drafted = draftState(origin, draft);

  const onCursor = useCallback((key) => {
    setCursorKey(key);
    if (draft && key !== itemKey) { setDraft(null); setNotice(DRAFT_DISCARDED); }
    // The compare epoch cannot be the one on screen.
    setCompareKey((c) => (c === key ? null : c));
  }, [itemKey, draft]);

  const step = useCallback((delta) => {
    setIndex((i) => Math.max(0, Math.min(items.length - 1, i + delta)));
  }, [items.length]);

  const submit = useCallback(async (v) => {
    if (!current || !gate.allowed || busy) return;
    setBusy(true); setNotice(null);
    try {
      const r = await recheckApi.verdict({
        item_id: current.item_id,
        assertion_type: v.assertion_type,
        reject_reason: v.reject_reason,
        note: v.note,
        viewed_dataset_key: cursorKey,
        compare_dataset_key: compareKey,
        ...(draft && { position: positionPayload(draft, cursorKey) }),
      });
      if (r.ok) {
        setDraft(null);
        setDecided((d) => ({ ...d, [current.item_id]: v.reject_reason ?? v.assertion_type }));
        const next = items.findIndex((it, i) => i > index && !decided[it.item_id]);
        if (next >= 0) setIndex(next);
      } else {
        const code = r.body?.code;
        setNotice(refusalText(r));
        if (code === "item_closed") setDecided((d) => ({ ...d, [current.item_id]: "closed" }));
        if (code === "lock_not_held") setLock({ state: "held_by_other", by: r.body?.locked_by ?? null });
      }
    } catch {
      setNotice("The verdict could not be sent.");
    } finally {
      setBusy(false);
    }
  }, [current, gate.allowed, busy, cursorKey, compareKey, items, index, decided, draft]);

  const decidedHere = current ? decided[current.item_id] : null;
  const canPlace = Boolean(origin) && gate.allowed && lock.state === "held" && !decidedHere && !busy;
  const startDraft = useCallback(() => {
    if (canPlace) setDraft((d) => d ?? [origin[0], origin[1]]);
  }, [canPlace, origin]);

  // In a layout effect, so the keys act on what is on screen: m right after
  // the buttons light up reaches the handler that can place a draft.
  useLayoutEffect(() => {
    const onKey = (e) => {
      switch (resolveRecheckKey(e)) {
        case "next": step(1); return;
        case "prev": step(-1); return;
        case "move": startDraft(); return;
        // The key map first: one Esc closes it and leaves a draft alone.
        case "cancel": if (helpOpen) setHelpOpen(false); else setDraft(null); return;
        case "help": e.preventDefault(); setHelpOpen((o) => !o); return;
        default:
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, startDraft, helpOpen]);

  if (page === "loading") {
    return <div className="p-6 text-fg-secondary"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading re-checks…</div>;
  }
  if (page !== "ready") {
    const text = {
      not_installed: "Re-checks are not installed on this environment yet (migration 141 is not applied).",
      forbidden: "Re-checks are restricted to DreamHammer reviewers.",
      error: "The re-check queue could not be read.",
    }[page];
    return (
      <div className="p-6 text-fg-secondary" data-testid="recheck-unavailable" data-state={page}>
        <AlertTriangle className="inline h-4 w-4 text-amber-300" /> {text}
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0" data-testid="recheck-page">
      <aside className="w-64 shrink-0 overflow-y-auto border-r border-bg-border">
        <div className="flex flex-col gap-1.5 border-b border-bg-border px-3 py-2" data-testid="recheck-aoi">
          <OperatingAoiPicker
            aois={aois}
            value={aoiId}
            onChange={onAoiChange}
            allLabel="All areas"
            renderMeta={(a) =>
              typeof a?.item_count === "number"
                ? `${a.item_count.toLocaleString()} of ${totalItems.toLocaleString()}`
                : ""
            }
          />
          {stats && (
            <span className="text-xs text-fg-secondary" data-testid="recheck-stats">
              {stats.outstanding.items.toLocaleString()} open in {stats.outstanding.cells.toLocaleString()} cells
            </span>
          )}
        </div>
        <div className="px-3 py-2 text-xs font-medium text-fg-secondary">Cells with open re-checks</div>
        {cells.length === 0 && <div className="px-3 py-2 text-xs text-fg-secondary">Nothing to re-check.</div>}
        {cells.map((c) => {
          const sel = hasCell && c.cell_x === x && c.cell_y === y;
          return (
            <button
              key={`${c.cell_x}:${c.cell_y}`} type="button"
              data-testid={`recheck-cell-${c.cell_x}-${c.cell_y}`}
              onClick={() => navigate({
                to: "/workspace/recheck/$cellX/$cellY",
                params: { cellX: String(c.cell_x), cellY: String(c.cell_y) },
                search: { aoi: aoiId ?? undefined },
              })}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${sel ? "bg-bg-soft text-fg-primary" : "text-fg-secondary"}`}
            >
              <span>{c.cell_x}, {c.cell_y}</span>
              <span className="text-xs">
                {Number(c.outstanding)}
                {c.locked_by && <Lock className="ml-1 inline h-3 w-3" aria-label="locked" />}
              </span>
            </button>
          );
        })}
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        {!hasCell && <div className="p-6 text-fg-secondary">Pick a cell to start. Opening a cell takes its lock.</div>}
        {hasCell && (
          <>
            <header className="flex items-center gap-3 border-b border-bg-border px-4 py-2 text-sm">
              <span className="text-fg-primary">Cell {x}, {y}</span>
              <span className="text-fg-secondary" data-testid="recheck-lock" data-state={lock.state}>
                {lock.state === "held" && "You hold this cell"}
                {lock.state === "taking" && "Taking the lock…"}
                {lock.state === "held_by_other" && "Another reviewer holds this cell (read-only)"}
                {lock.state === "error" && "The lock could not be taken (read-only)"}
              </span>
              {items.length > 0 && (
                <span className="ml-auto text-fg-secondary">
                  Item {index + 1} of {items.length}
                  {current && <> · {current.kind === "presence" ? "presence check" : "re-observation"}</>}
                </span>
              )}
            </header>

            {notice && <div className="px-4 py-1.5 text-xs text-amber-300" data-testid="recheck-notice">{notice}</div>}

            <div className="relative min-h-0 flex-1">
              <ReviewTimeStripMap
                point={detail?.item?.geom ?? null}
                datasetKey={cursorKey}
                compareKey={compareKey}
                label={epochLabel(lanes, cursorKey)}
                compareLabel={epochLabel(lanes, compareKey)}
                draft={draft}
                distanceLabel={drafted ? formatMeters(drafted.distance_m) : ""}
                amber={Boolean(drafted?.amber)}
                onDraftMove={setDraft}
              />
            </div>

            <footer className="flex flex-col gap-3 border-t border-bg-border px-4 py-3">
              {detail && (
                <TimeStrip
                  lanes={lanes} cursorKey={cursorKey} itemKey={itemKey} compareKey={compareKey}
                  onCursor={onCursor} onCompare={setCompareKey}
                />
              )}
              {detail && laneOf(lanes, cursorKey) === "reference" && (
                <div className="text-xs text-fg-secondary">Viewing Sentinel-2: context only.</div>
              )}
              {decidedHere ? (
                <div className="text-sm text-emerald-300" data-testid="recheck-decided">
                  <CheckCircle2 className="inline h-4 w-4" /> Answered ({decidedHere.replace("_", " ")}).
                </div>
              ) : (
                current && (
                  <RecheckVerdictBar
                    gate={gate} lockHeld={lock.state === "held"} busy={busy || !detail}
                    resetKey={current.item_id} draft={drafted} onSubmit={submit}
                  />
                )
              )}
              <div className="flex gap-2">
                <button type="button" className={FOOTER_BUTTON} onClick={() => step(-1)} disabled={index === 0}>Previous (k)</button>
                <button type="button" className={FOOTER_BUTTON} onClick={() => step(1)} disabled={index >= items.length - 1}>Next (j)</button>
                {draft
                  ? <button type="button" className={FOOTER_BUTTON} onClick={() => setDraft(null)}>Discard position (Esc)</button>
                  : <button type="button" className={FOOTER_BUTTON} onClick={startDraft} disabled={!canPlace}>Correct position (m)</button>}
              </div>
            </footer>
          </>
        )}
      </section>
      <ShortcutHelp
        open={helpOpen} onClose={() => setHelpOpen(false)}
        actions={RECHECK_KEY_ACTIONS} groups={RECHECK_KEY_GROUPS}
      />
    </div>
  );
}
